import { z } from 'zod'
import { ReportDateSchema } from './reports'

/**
 * Audit browser — admin-only. Exists primarily so a shop owner can answer
 * "who removed what, and is anyone removing an unusual amount", which is the
 * control that makes delegating the hard delete (handlers/records-delete.ts)
 * safe: a deleted record leaves no row behind, so this read is the ONLY way to
 * see it happened from inside the product.
 *
 * The underlying items need no new index: audit rows live in the tenant
 * partition under `AUDIT#<iso>#<ulid>` (see keys.auditSk in lib/ddb.ts), so a
 * date range is one bounded Query with BETWEEN, and grouping by actor happens
 * in-Lambda. At shop scale (hundreds of actions a month) that's well inside a
 * single request — revisit only if a tenant's audit volume outgrows it.
 */
/**
 * Every action the audit log records — the single source of truth, consumed by
 * `AuditAction` in lib/audit.ts (the writer) and by the filter below (the
 * reader). Adding an action here makes it both writable and filterable; keeping
 * two lists would let the browser silently lose sight of a new action.
 *
 * Note this log spans the whole application, not just records: sales, returns,
 * orders, suppliers, settings and user administration all write to it.
 */
export const AUDIT_ACTIONS = [
  'RECORD_INTAKED',
  'RECORD_UPDATED',
  'RECORD_SCRAPPED',
  'RECORD_DELETED',
  'RECORD_TRANSITIONED',
  'RECORD_MOVED',
  'SUPPLIER_CREATED',
  'SUPPLIER_UPDATED',
  'SUPPLIER_DELETED',
  'SUPPLIER_RESTORED',
  'SALE_COMPLETED',
  'SALE_REVERSED',
  'RECEIPT_EMAILED',
  'RETURN_PROCESSED',
  'FULFILMENT_COMPLETED',
  'ORDER_STATUS_CHANGED',
  'ORDER_MESSAGE_SENT',
  'ORDER_NOTE_SET',
  'ORDER_REFUNDED',
  'SETTINGS_UPDATED',
  'USER_INVITED',
  'USER_DISABLED',
  'STAFF_RENAMED',
  // Webshop storefront administration. RECORD_(UN)PUBLISHED are RETIRED
  // writers (ADR 0003: visibility is derived from channel + price; nothing
  // publishes explicitly any more) but stay in the enum read-only so
  // historical audit rows keep parsing and the Rapportage filter keeps
  // labelling them.
  'RECORD_PUBLISHED',
  'RECORD_UNPUBLISHED',
  'WEBSHOP_SETTINGS_UPDATED',
  'CONTENT_PAGE_SAVED',
  'CONTENT_PAGE_DELETED',
] as const

export const AuditActionSchema = z.enum(AUDIT_ACTIONS).openapi('AuditAction')
export type AuditAction = z.infer<typeof AuditActionSchema>

/**
 * Every kind of entity an audit row can point at — the same single-source-of-
 * truth treatment as `AUDIT_ACTIONS` above, and for the same reason: the
 * browser labels each type, so a new one that only existed as a string literal
 * at the write site would render as "unknown" with nothing failing.
 *
 * Deliberately NOT applied to `AuditEntrySchema.target.type` on the response
 * side, which stays `z.string()`: the log is append-only history and can hold
 * rows written under a type that has since been retired.
 */
export const AUDIT_TARGET_TYPES = [
  'record',
  'supplier',
  'transaction',
  'order',
  'staff',
  'settings',
  // Webshop storefront administration. Both are admin-driven and carry a
  // `userSub` actor; they are listed here for the same reason as the rest —
  // a write-site literal that is not a member renders as "unknown" in the
  // audit browser with nothing failing.
  'content-page',
  'webshop-settings',
] as const

/** Derived from the const directly — no zod schema, because nothing validates
 *  against it: it constrains the WRITE site at compile time, and the response
 *  side deliberately stays `z.string()`. Registering an OpenAPI component here
 *  would publish a type no path references. */
export type AuditTargetType = (typeof AUDIT_TARGET_TYPES)[number]

/**
 * What the browser can narrow to: any single action, plus two shorthands.
 * `all` is the default; `removals` spans the three ways a copy leaves the shelf,
 * which cannot be expressed as one action because mark-missing rides
 * `RECORD_TRANSITIONED` (see RemovalOutcomeSchema).
 */
export const AuditActionFilterSchema = z
  .enum(['all', 'removals', ...AUDIT_ACTIONS])
  .openapi('AuditActionFilter')
export type AuditActionFilter = z.infer<typeof AuditActionFilterSchema>

/**
 * The three ways a copy leaves the shelf, as the removal modal presents them.
 *
 * NOT an action allowlist: delete and scrap have dedicated audit actions, but
 * mark-missing rides `RECORD_TRANSITIONED` alongside sold/reserved/returned, so
 * it is identified by the resulting status instead (see `removalOutcomeOf` in
 * handlers/audit.ts). Filtering on actions alone made mark-missing invisible to
 * the `removals` filter and absent from the per-actor rollup — i.e. an operator
 * could mark stock missing indefinitely and show a total of zero.
 */
export const RemovalOutcomeSchema = z.enum(['delete', 'scrap', 'missing']).openapi('RemovalOutcome')
export type RemovalOutcome = z.infer<typeof RemovalOutcomeSchema>

export const AuditQuerySchema = z
  .object({
    from: ReportDateSchema,
    to: ReportDateSchema,
    action: AuditActionFilterSchema.default('all'),
    /** Narrow to one staff member (raw Cognito sub). */
    actor: z.string().min(1).max(128).optional(),
    /**
     * One RECORD's COMPLETE timeline — every event it went through since
     * intake. When set, `from`/`to` are deliberately IGNORED: "when did this
     * record arrive" is the question, and that may be years outside any
     * window. Served from the RECHIST pointer items (see lib/audit.ts), not a
     * scan of the date range, so age costs nothing.
     *
     * RECORD ids only, despite the generic name: pointers are written only for
     * record targets, so a supplier/order/transaction id yields an empty
     * result that reads exactly like "no history" rather than "unsupported".
     */
    targetId: z.string().min(1).max(128).optional(),
  })
  .refine(v => v.from <= v.to, { message: '"from" must not be after "to"', path: ['from'] })
  .openapi('AuditQuery')
export type AuditQuery = z.infer<typeof AuditQuerySchema>

/**
 * One changed field, as "from → to". Values are stringified scalars — the
 * browser renders them (translating known enums like status/channel); nested
 * objects and arrays are never diffed (see `changesOf` in handlers/audit.ts).
 */
export const AuditChangeSchema = z
  .object({
    field: z.string(),
    // 201 = the handler's CHANGE_VALUE_MAX plus the "…" it appends when it
    // clips. Bounding it here makes the clamp part of the published contract,
    // not just handler behaviour a future edit could drop.
    from: z.string().max(201).nullable(),
    to: z.string().max(201).nullable(),
  })
  .openapi('AuditChange')
export type AuditChange = z.infer<typeof AuditChangeSchema>

/**
 * One audit row as shown to an admin.
 *
 * Deliberately NOT the raw stored item: `before`/`after` hold full entity
 * snapshots (tens of KB, and business/customer data the browser has no need
 * for). We project a compact summary instead — enough to answer "which record"
 * without shipping the whole thing.
 */
export const AuditEntrySchema = z
  .object({
    id: z.string(),
    timestamp: z.string(),
    action: z.string(),
    /** Raw Cognito sub. */
    actor: z.string(),
    /**
     * Resolved via lib/staff.ts. `null` means neither an admin-set name nor a
     * cached email was found for that sub — the UI falls back to the raw id.
     */
    actorDisplayName: z.string().nullable(),
    target: z.object({ type: z.string(), id: z.string() }),
    /**
     * Compact description of the affected record, reconstructed from the audit
     * item's `before` snapshot. Null for non-record actions, or when the
     * snapshot predates this projection.
     */
    recordSummary: z
      .object({
        shortId: z.string().nullable(),
        artist: z.string().nullable(),
        title: z.string().nullable(),
      })
      .nullable(),
    /**
     * How the target's OWN screen addresses it, when that differs from
     * `target.id`. Only orders need it today: audit rows key on the order's
     * ULID (the entity identity, like every other target type), but the
     * Bestellingen page addresses an order by its `channelOrderId` — the same
     * value for a webshop order, a Discogs order id for a Discogs one. Null
     * when `target.id` is already the right key, and for historical rows
     * written before the writers recorded it.
     */
    targetRef: z.string().nullable().default(null),
    /**
     * A short human-identifiable name for whatever the action touched — the
     * supplier's name, the staff member's display name, the order's human
     * number (`RV-2026-0834`) or failing that the channel order id,
     * and for a record its "artist — title".
     *
     * Deliberately a bare identifier and NOT a rendered sentence: the log spans
     * six target types, and phrasing them ("Verkoop van €19,99 aan de balie")
     * is translation work that belongs in the frontend, which has both locales.
     * Null when the entity has no natural name — a transaction or the settings
     * singleton — where the UI falls back to `target.id` and the timestamp.
     */
    targetLabel: z.string().nullable(),
    /**
     * What actually changed, for update-shaped actions — "status: reserved →
     * in_stock" instead of a bare "Status gewijzigd". Computed from the stored
     * `before`/`after` snapshots at read time (so it covers rows written before
     * this field existed), capped at `AUDIT_CHANGES_CAP` scalar fields. Null
     * for actions that aren't a field-level edit (intake, sale, receipt, …).
     *
     * `changesTruncated` means the response omits some detail, and has TWO
     * producers: the per-row `AUDIT_CHANGES_CAP` was hit (so `changes` holds
     * the first six of more), or the response-wide `CHANGE_PAYLOAD_BUDGET` was
     * spent, in which case `changes` is NULL even though the row had a diff.
     * So `{ changes: null, changesTruncated: true }` is reachable and means
     * "there was detail, we returned none of it" — do not read a truncated row
     * as necessarily carrying six changes.
     *
     * `.default()` rather than required: deploys are manual and per-stack, so
     * the SPA can ship before the API. A frontend that hard-required these
     * fields turned that ordinary deploy gap into a full audit-tab outage
     * (every parse throwing); with defaults, an older backend's rows simply
     * render without the diff line until the API catches up.
     */
    changes: z.array(AuditChangeSchema).nullable().default(null),
    changesTruncated: z.boolean().default(false),
    /**
     * Which removal this row represents, or null if it isn't one. Lets the UI
     * label a row without re-deriving the action→outcome rule.
     */
    removalOutcome: RemovalOutcomeSchema.nullable(),
    /**
     * Removal reason. Read from `metadata` for a hard delete (no row survives
     * to hold it) and from the `after` snapshot for scrap / mark-missing, which
     * persist it on the record itself.
     */
    removalReason: z.string().nullable(),
    removalReasonNote: z.string().nullable(),
  })
  .openapi('AuditEntry')
export type AuditEntry = z.infer<typeof AuditEntrySchema>

/**
 * Per-actor rollup over the SAME filtered window as `entries`, so an admin can
 * spot an outlier without counting rows by hand — the "unusual amount"
 * question, answered directly.
 */
export const AuditActorTotalSchema = z
  .object({
    actor: z.string(),
    actorDisplayName: z.string().nullable(),
    total: z.number().int(),
    deleted: z.number().int(),
    scrapped: z.number().int(),
    /**
     * Mark-missing count. Present for the same reason the outcome exists at
     * all: without it, quietly marking stock missing is the one removal that
     * never shows up in the "is anyone removing an unusual amount" answer.
     */
    missing: z.number().int(),
  })
  .openapi('AuditActorTotal')
export type AuditActorTotal = z.infer<typeof AuditActorTotalSchema>

export const AuditResponseSchema = z
  .object({
    entries: z.array(AuditEntrySchema),
    byActor: z.array(AuditActorTotalSchema),
    /**
     * True when the read cap was hit and `entries` is therefore incomplete.
     * Surfaced rather than silently truncated so the UI can tell the admin to
     * narrow the range instead of quietly under-reporting a removal count.
     */
    truncated: z.boolean(),
  })
  .openapi('AuditResponse')
export type AuditResponse = z.infer<typeof AuditResponseSchema>
